import Phaser from 'phaser';
import { GAME_HEIGHT, GAME_WIDTH } from '../data/config';

/**
 * True when the device is a phone/tablet that should see the on-screen
 * touch joystick. No single signal is reliable on the modern web —
 * pointer media queries miss devices with both stylus and touch, UA
 * strings can lie, etc. So we layer multiple checks. Any positive signal
 * = mobile. Defaults to NO when nothing matches (keeps controls hidden
 * on plain desktops without surprising anyone).
 *
 *   1. URL override        — ?touch forces on, ?notouch forces off
 *   2. Client Hints        — navigator.userAgentData.mobile (modern Chrome)
 *   3. UA regex            — Android/iPhone/iPad/etc. catch-all
 *   4. Primary pointer     — (pointer: coarse) primary input is touch
 *   5. Touch events + no   — has ontouchstart and no fine pointer at all
 *      fine pointer at all
 *
 * Each signal can independently flip the result to true, so e.g. a Samsung
 * phone with S-Pen (which reports any-pointer: fine) still gets caught
 * by the UA regex or primary-pointer check.
 */
export function isTouchDevice(): boolean {
  if (typeof window === 'undefined') return false;
  const search = window.location?.search ?? '';
  if (search.includes('notouch')) return false;
  if (search.includes('touch')) return true;

  // 1. Modern Client Hints API (Chrome/Edge). The most reliable single
  // signal when available — UA strings have to be fingerprinted, but
  // userAgentData.mobile is a clean boolean.
  const uaData = (navigator as Navigator & { userAgentData?: { mobile?: boolean } }).userAgentData;
  if (uaData?.mobile === true) return true;

  // 2. UA regex — covers basically every phone/tablet that reports a
  // recognizable user agent. Doesn't catch laptops-pretending-to-be-
  // mobile via DevTools emulation, but those aren't a real-user case.
  const ua = navigator.userAgent || '';
  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|Tablet/i.test(ua)) {
    return true;
  }

  // 3. iPadOS 13+ reports as Mac in UA, but has touch points.
  if (/Macintosh/i.test(ua) && (navigator.maxTouchPoints ?? 0) > 1) return true;

  // 4. Primary pointer is coarse — phone/tablet primary input is touch.
  if (typeof window.matchMedia === 'function') {
    if (window.matchMedia('(pointer: coarse)').matches) return true;

    // 5. Has touch events AND no fine pointer at all → almost certainly
    // a touch-only device that lied about its UA.
    const hasTouchEvents =
      'ontouchstart' in window || (navigator.maxTouchPoints ?? 0) > 0;
    const noFinePointer = !window.matchMedia('(any-pointer: fine)').matches;
    if (hasTouchEvents && noFinePointer) return true;
  }

  return false;
}

/**
 * Lightweight virtual joystick + jump/kick buttons.
 * Reads multitouch from Phaser's pointer pool so each control is independent.
 *
 *  - Joystick: bottom-left fixed position (drag knob to indicate direction).
 *  - Jump button: bottom-right.
 *  - Kick button: above jump.
 *
 * Exposes:
 *   axisX  — horizontal joystick value in [-1, 1]
 *   jumpPressed / kickPressed — true on the frame the button is freshly pressed
 *   jumpHeld / kickHeld       — true while held
 */
export class TouchControls {
  private scene: Phaser.Scene;
  private base: Phaser.GameObjects.Image;
  private knob: Phaser.GameObjects.Image;
  private jumpBtn: Phaser.GameObjects.Image;
  private kickBtn: Phaser.GameObjects.Image;
  private jumpLabel: Phaser.GameObjects.Text;
  private kickLabel: Phaser.GameObjects.Text;

  private stickPointer: Phaser.Input.Pointer | null = null;
  private stickCenter = new Phaser.Math.Vector2(0, 0);
  private radius = 28;
  private enabled = true;

  axisX = 0;
  jumpHeld = false;
  kickHeld = false;
  jumpPressed = false;
  kickPressed = false;
  private prevJump = false;
  private prevKick = false;

  /**
   * If `visible` is omitted we auto-detect: show on any touch-capable
   * device. Earlier we required `(pointer: coarse) AND (hover: none)`
   * but that was too strict — iPads in some modes, Surface tablets, and
   * various Android phones report `hover: hover` (because they CAN
   * hover via stylus / external mouse) and got missed. New rule: show
   * if EITHER coarse-pointer OR multi-touch input is reported. Force
   * via `?touch` URL param for testing on desktop.
   */
  constructor(scene: Phaser.Scene, opts: { visible?: boolean } = {}) {
    this.scene = scene;
    const visible = opts.visible ?? isTouchDevice();
    this.enabled = visible;

    const stickX = 56;
    const stickY = GAME_HEIGHT - 56;
    this.stickCenter.set(stickX, stickY);

    this.base = scene.add
      .image(stickX, stickY, 'stick-base')
      .setScrollFactor(0)
      .setDepth(1000)
      .setVisible(visible);
    this.knob = scene.add
      .image(stickX, stickY, 'stick-knob')
      .setScrollFactor(0)
      .setDepth(1001)
      .setVisible(visible);

    const btnX = GAME_WIDTH - 38;
    const jumpY = GAME_HEIGHT - 38;
    const kickY = GAME_HEIGHT - 86;

    this.jumpBtn = scene.add
      .image(btnX, jumpY, 'btn-bg')
      .setScrollFactor(0)
      .setDepth(1000)
      .setVisible(visible)
      .setInteractive({ useHandCursor: true });
    this.kickBtn = scene.add
      .image(btnX, kickY, 'btn-bg')
      .setScrollFactor(0)
      .setDepth(1000)
      .setVisible(visible)
      .setInteractive({ useHandCursor: true });

    this.jumpLabel = scene.add
      .text(btnX, jumpY, 'JUMP', {
        fontFamily: '"VT323", monospace',
        fontSize: '11px',
        color: '#ffffff',
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(1002)
      .setVisible(visible);
    this.kickLabel = scene.add
      .text(btnX, kickY, 'FIRE', {
        fontFamily: '"VT323", monospace',
        fontSize: '11px',
        color: '#ffffff',
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(1002)
      .setVisible(visible);

    // Button handlers.
    const press = (btn: Phaser.GameObjects.Image, set: (v: boolean) => void) => {
      btn.on('pointerdown', () => {
        set(true);
        scene.tweens.add({ targets: btn, scale: 0.92, duration: 60, yoyo: true });
      });
      btn.on('pointerup', () => set(false));
      btn.on('pointerout', () => set(false));
      btn.on('pointerupoutside', () => set(false));
    };
    press(this.jumpBtn, (v) => (this.jumpHeld = v));
    press(this.kickBtn, (v) => (this.kickHeld = v));

    // Joystick — listen on the whole input layer so we can grab any pointer
    // that lands inside the joystick circle. Only active when controls are
    // enabled; otherwise desktop clicks in the bottom-left would steer the
    // dino invisibly.
    scene.input.on('pointerdown', (p: Phaser.Input.Pointer) => {
      if (!this.enabled) return;
      const dist = Phaser.Math.Distance.Between(p.x, p.y, this.stickCenter.x, this.stickCenter.y);
      if (dist <= 60 && !this.stickPointer) {
        this.stickPointer = p;
        this.updateStick(p);
      }
    });
    scene.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (!this.enabled) return;
      if (this.stickPointer && p.id === this.stickPointer.id) {
        this.updateStick(p);
      }
    });
    const release = (p: Phaser.Input.Pointer) => {
      if (!this.enabled) return;
      if (this.stickPointer && p.id === this.stickPointer.id) {
        this.stickPointer = null;
        this.knob.setPosition(this.stickCenter.x, this.stickCenter.y);
        this.axisX = 0;
      }
    };
    scene.input.on('pointerup', release);
    scene.input.on('pointerupoutside', release);
  }

  private updateStick(p: Phaser.Input.Pointer) {
    const cam = this.scene.cameras.main;
    // Convert page-space pointer to canvas-space, accounting for camera/scale.
    const px = p.x;
    const py = p.y;
    const dx = px - this.stickCenter.x;
    const dy = py - this.stickCenter.y;
    const len = Math.hypot(dx, dy);
    const r = Math.min(len, this.radius);
    const ang = Math.atan2(dy, dx);
    const kx = this.stickCenter.x + Math.cos(ang) * r;
    const ky = this.stickCenter.y + Math.sin(ang) * r;
    this.knob.setPosition(kx, ky);
    this.axisX = (kx - this.stickCenter.x) / this.radius;
    void cam; // silence unused
  }

  /** Call once per scene update before consuming axes. */
  beforeUpdate() {
    this.jumpPressed = this.jumpHeld && !this.prevJump;
    this.kickPressed = this.kickHeld && !this.prevKick;
  }

  /** Call once per scene update after consuming axes. */
  afterUpdate() {
    this.prevJump = this.jumpHeld;
    this.prevKick = this.kickHeld;
  }

  setVisible(v: boolean) {
    [this.base, this.knob, this.jumpBtn, this.kickBtn, this.jumpLabel, this.kickLabel].forEach(
      (o) => o.setVisible(v),
    );
  }
}
